import {useEffect, useState} from 'react'
import {useMatch, useNavigate, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup'

import {Modal} from '../../../../../_metronic/elements/Modal'
import {InputText} from '../../../../../_metronic/elements/Inputs'
import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import {openAlert} from '../../../../../_metronic/elements/NotificationAlert'
import {useMapLayersV2} from '../../Map/MapLayersV2Context'
import {useDrawV2} from '../../Map/Draw/DrawV2Context'
import {useDrawEndButton} from '../../Map/Draw/DrawEndButton/DrawEndButtonContext'
import {DrawTools} from '../../Map/Draw/DrawState'
import {useViewFieldContext} from '../ViewField/ViewFieldContext'
import * as FieldService from '../services/FieldService'
import {useSearchContext} from '../../Toolbar/SearchContext'
import {useIsMobile} from '../../../../shared/responsive'
import '../CreateFlow.style.scss'

const ModalHeader = () => {
  const {t} = useTranslation()

  return (
    <div className='modal-header p-5'>
      <h5 className='modal-title'>{t('fields:update.title')}</h5>
    </div>
  )
}

const ModalBody = ({formik, loading, polygonUpdated, updatePolygon, field}) => {
  const {t} = useTranslation()

  return (
    <div className='modal-body p-5'>
      <div className='row'>
        <div className='col-lg-12 fv-row'>
          <InputText
            disabled={loading}
            formik={formik}
            name='name'
            inputProps={{
              placeholder: t('fields:create.input.name.placeholder'),
            }}
          />
          {field.polygons && (
            <button
              onClick={updatePolygon}
              type='button'
              className={`btn ${polygonUpdated ? 'btn-light-success' : 'btn-light-primary'} mt-5`}
              style={{width: '100%'}}
            >
              {t('fields:update.buttons.update_polygon')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const ModalFooter = ({loading, cancel}) => {
  const {t} = useTranslation()

  return (
    <div className='modal-footer'>
      <button disabled={loading === true} type='button' className='btn btn-light' onClick={cancel}>
        {t('fields:update.buttons.cancel')}
      </button>
      <button type='submit' className='btn btn-primary'>
        {!loading && t('fields:update.buttons.update')}
        {loading && (
          <span className='indicator-progress' style={{display: 'block'}}>
            {t('fields:update.messages.updating')}{' '}
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          </span>
        )}
      </button>
    </div>
  )
}

const MobileUpdateFieldCard = ({
  field,
  formik,
  loading,
  polygonUpdated,
  updatePolygon,
  cancel,
}) => {
  const {t} = useTranslation()

  return (
    <div className='field-view-mobile-shell displayable'>
      <form onSubmit={formik.handleSubmit} className='form'>
        <div className='card shadow-sm right-card-data gis-create-flow-card'>
          <div className='card-header'>
            <p className='gis-create-flow-eyebrow'>{t('fields:labels.field')}</p>
            <h4 className='gis-create-flow-title'>{t('fields:update.title')}</h4>
          </div>

          <div className='card-body'>
            <div className='gis-create-flow-block'>
              <div className='gis-create-flow-input'>
                <p className='gis-create-flow-label'>
                  {t('fields:update.mobile.edit.name_label')}
                </p>
                <InputText
                  disabled={loading}
                  formik={formik}
                  name='name'
                  inputProps={{
                    placeholder: t('fields:create.input.name.placeholder'),
                  }}
                />
              </div>

              {field.polygons && (
                <button
                  onClick={updatePolygon}
                  type='button'
                  className={`btn ${polygonUpdated ? 'btn-light-success' : 'btn-light-primary'} w-100`}
                >
                  {t('fields:update.buttons.update_polygon')}
                </button>
              )}
            </div>
          </div>

          <div className='card-footer'>
            <button
              disabled={loading === true}
              type='button'
              className='btn btn-light gis-create-flow-footer-button'
              onClick={cancel}
            >
              {t('fields:update.buttons.cancel')}
            </button>
            <button type='submit' className='btn btn-primary gis-create-flow-footer-button'>
              {!loading && t('fields:update.buttons.update')}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {t('fields:update.messages.updating')}{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

const UpdateField = ({open, setOpen}) => {
  const {field, setField} = useViewFieldContext()
  const [polygonUpdated, setPolygonUpdated] = useState(false)
  const [polygon, setPolygon] = useState(null)
  const [loading, setLoading] = useState(false)
  const {field_id} = useParams()
  const {errorHandler} = useErrorHandler()
  const {t} = useTranslation()
  const mapLayers = useMapLayersV2()
  const drawV2 = useDrawV2()
  const drawEndButton = useDrawEndButton()
  const search = useSearchContext()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const routeMatch = useMatch('/gis/field/view/:field_id/update')
  const isRouteDriven = isMobile && routeMatch != null
  const isEditorOpen = isRouteDriven ? !drawV2.isDrawing : open

  const schema = Yup.object().shape({
    name: Yup.string()
      .min(1, t('fields:create.input.name.errors.min'))
      .max(32, t('fields:create.input.name.errors.max'))
      .required(t('fields:create.input.name.errors.required')),
  })

  const formik = useFormik({
    initialValues: {
      name: field?.name || '',
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, {setFieldValue}) => {
      setLoading(true)

      const extra = {}
      if (polygon?.geometry) {
        extra.polygons = polygon.geometry
      }

      const res = await errorHandler(FieldService.update(field_id, {name: values.name, ...extra}))

      if (res.ok) {
        mapLayers.reloadSource()
        search.load()
        setField(res.data)
        openAlert('success', t('fields:update.messages.success_title'), t('fields:update.messages.success'))
        setFieldValue('name', res.data.name)
        cancel()
        navigate(`/gis/field/view/${field_id}`)
      }

      setLoading(false)
    },
  })

  const reopenEditor = (nextPolygon) => {
    setPolygon(nextPolygon)
    setPolygonUpdated(Boolean(nextPolygon?.geometry))

    if (!isRouteDriven) {
      setOpen(true)
    }
  }

  const updatePolygon = () => {
    setPolygonUpdated(true)
    mapLayers.field.hide(field_id)

    let polygonId = polygon?.id

    if (!polygonId) {
      polygonId = drawV2.add(field.polygons)
      setPolygon({
        id: polygonId,
        geometry: field.polygons,
      })
      mapLayers.fit(field_id)
    }

    drawV2.startDrawing({
      tools: [DrawTools.POLYGON],
    })
    drawV2.directSelect(polygonId)

    if (!isRouteDriven) {
      setOpen(false)
    }

    openAlert('info', t('fields:update.update_polygon.title'), t('fields:update.update_polygon.message'))

    drawEndButton.show({
      title: t('fields:update.update_polygon.updated_polygon'),
      buttonText: t('fields:update.buttons.end_edition'),
      onFinish: () => {
        const updatedFeature = drawV2.get(polygonId)
        drawEndButton.hide()
        drawV2.endDrawing()

        if (!updatedFeature?.geometry) {
          reopenEditor(null)
          return
        }

        reopenEditor({
          id: polygonId,
          geometry: updatedFeature.geometry,
        })
      },
      onCancel: () => {
        drawEndButton.hide()
        drawV2.endDrawing()
        setPolygon(null)
        setPolygonUpdated(false)
        mapLayers.field.show(field_id)

        if (!isRouteDriven) {
          setOpen(true)
        }
      },
    })
  }

  const cancel = () => {
    setPolygonUpdated(false)
    setPolygon(null)
    mapLayers.field.show(field_id)
    drawEndButton.hide()
    drawV2.endDrawing()
    drawV2.deleteAllLayers()
    formik.resetForm()
    if (isRouteDriven) {
      navigate(`/gis/field/view/${field_id}`)
      return
    }

    setOpen(false)
  }

  useEffect(() => {
    if (polygon?.geometry) {
      setPolygonUpdated(true)
      return
    }

    setPolygonUpdated(false)
  }, [polygon])

  if (isMobile) {
    if (!isEditorOpen) {
      return null
    }

    return (
      <MobileUpdateFieldCard
        field={field}
        formik={formik}
        loading={loading}
        polygonUpdated={polygonUpdated}
        updatePolygon={updatePolygon}
        cancel={cancel}
      />
    )
  }

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <form onSubmit={formik.handleSubmit} className='form'>
            <ModalHeader />
            <ModalBody
              formik={formik}
              field={field}
              loading={loading}
              polygonUpdated={polygonUpdated}
              updatePolygon={updatePolygon}
            />
            <ModalFooter loading={loading} cancel={cancel} />
          </form>
        </div>
      </div>
    </Modal>
  )
}

export default UpdateField
